/**
 * Incident documentation stays hidden unless GET /api/v1/features says incidentDocs is true.
 * The default is off, so a slow or failed features response does not call /api/v1/incidents.
 */
import { useEffect, useState } from "react";

export type IncidentDocsState = "unknown" | "on" | "off";

let state: IncidentDocsState = "unknown";
let bcState: IncidentDocsState = "unknown";
const listeners = new Set<() => void>();

export function getIncidentDocsState(): IncidentDocsState {
  return state;
}

export function setIncidentDocsState(next: IncidentDocsState) {
  state = next;
  listeners.forEach((listener) => listener());
}

export function getIncidentDocsBcState(): IncidentDocsState {
  return bcState;
}

export function setIncidentDocsBcState(next: IncidentDocsState) {
  bcState = next;
  listeners.forEach((listener) => listener());
}

export function incidentDocsBcOn(current: IncidentDocsState = bcState): boolean {
  return current === "on";
}

export function parseIncidentDocsBc(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  const data = (payload as { data?: { incidentDocsBc?: unknown } }).data;
  return data?.incidentDocsBc === true;
}

export function useIncidentDocsBc(): IncidentDocsState {
  const [current, setCurrent] = useState(getIncidentDocsBcState);
  useEffect(() => subscribeIncidentDocs(() => setCurrent(getIncidentDocsBcState())), []);
  return current;
}

export function subscribeIncidentDocs(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function resetIncidentDocsState(next: IncidentDocsState = "unknown") {
  setIncidentDocsState(next);
  setIncidentDocsBcState(next);
}

export function parseIncidentDocs(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  const data = (payload as { data?: { incidentDocs?: unknown } }).data;
  return data?.incidentDocs === true;
}

export function incidentDocsOn(current: IncidentDocsState = state): boolean {
  return current === "on";
}

export type IncidentRouteDecision = "wait" | "redirect" | "show";

export function incidentRouteDecision(current: IncidentDocsState): IncidentRouteDecision {
  if (current === "on") return "show";
  if (current === "off") return "redirect";
  return "wait";
}

export function navigationWithoutIncidents<T extends { path: string }>(items: T[], enabled: boolean): T[] {
  if (enabled) return items;
  return items.filter((item) => item.path !== "/incidents");
}

export function shouldRequestIncidents(enabled: boolean): boolean {
  return enabled === true;
}

export async function lookupIncidentForAlert<T>(
  enabled: boolean,
  alertId: string,
  list: (query: { alert_id: string }) => Promise<T[]>
): Promise<T | null> {
  if (!shouldRequestIncidents(enabled)) return null;
  const rows = await list({ alert_id: alertId });
  return rows[0] || null;
}

export function useIncidentDocs(): IncidentDocsState {
  const [current, setCurrent] = useState(getIncidentDocsState);
  useEffect(() => subscribeIncidentDocs(() => setCurrent(getIncidentDocsState())), []);
  return current;
}
